import CustomHead from '@/components/CustomHead';
import AuthForm from '@/components/Forms/auth';
import StudentRoomLoginForm from '@/components/Forms/StudentRoomLoginForm';
import { BrandLogo } from '@/components/BrandLogo';
import { Logo } from '@/components/icons';
import {
  determineRedirectionPath,
  getServerSession,
} from '@/lib/Auth/GetServerSession';
import { CSPostHogProvider } from '@/lib/Hooks/PostHogProvider';
import { WebViewProvider } from '@/lib/Hooks/useIsWebViewContext';
import getQueryClientConfig from '@/lib/react-query/queryClientConfig';
import { applyForcedLogoutHeaders } from '@/lib/Auth/sessionIntegrity';
import { authOptions } from '@/lib/Auth/authOptions';
import { useBrand } from '@/lib/brand/BrandContext';
import { filterEnabledAuthProviders } from '@/lib/brand/filterAuthProviders';
import { getServerBrandContext } from '@lib-server/brand/getServerBrandContext';
import { cn } from '@/lib/utils';
import { recordStudentReportLoginEvent } from '@/lib/SessionTracking/recordStudentReportLoginEvent';
import { useSessionManager } from '@/lib/SessionTracking/useSession';
import { STUDENT_REPORT_LOGIN_EVENT_NAMES } from '@/utility/studentReportLoginTracking';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { GetServerSidePropsContext } from 'next';
import { getProviders } from 'next-auth/react';
import Link from 'next/link';
import { useState } from 'react';

const AUTH_SECRET = process.env.NEXTAUTH_SECRET || process.env.SECRET;
type AuthProviders = Awaited<ReturnType<typeof getProviders>>;

const SignIn = ({ providers }: { providers: AuthProviders }) => {
  const brand = useBrand();
  const sessionManager = useSessionManager();
  const [loginMode, setLoginMode] = useState<'educator' | 'student'>(
    'educator'
  );
  const [queryClient] = useState(() => new QueryClient(getQueryClientConfig()));
  const isStudentMode = loginMode === 'student';
  const isNyla = brand.brandKey === 'nyla';
  const homeHref = isNyla ? '/' : 'https://exploremindlabs.com';
  const getLoginModeButtonClass = (active: boolean) =>
    cn(
      'flex-1 rounded-full px-4 py-2 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
      isNyla && 'nyla-display',
      active
        ? 'bg-white text-gray-900 shadow'
        : 'text-gray-600 hover:text-gray-900'
    );
  const handleStudentMode = () => {
    if (!isStudentMode) {
      recordStudentReportLoginEvent(
        sessionManager,
        STUDENT_REPORT_LOGIN_EVENT_NAMES.ROOM_CODE_TAB_CLICKED,
        { source_context: 'signin' }
      );
    }
    setLoginMode('student');
  };

  return (
    <WebViewProvider>
      <CSPostHogProvider>
        <QueryClientProvider client={queryClient}>
          <CustomHead title={`Sign In - ${brand.appName}`} />
          <div className="flex min-h-screen flex-col bg-white text-gray-900">
            <header
              className={cn(
                'mx-auto flex w-full px-4 sm:px-6 lg:px-8',
                isNyla ? 'justify-center pb-5 pt-10' : 'max-w-6xl py-8'
              )}
            >
              {isNyla ? (
                <div className="flex items-center">
                  <Link href={homeHref} aria-label={`${brand.appName} home`}>
                    <BrandLogo className="h-16 w-36 min-[360px]:h-20 min-[360px]:w-44 sm:h-24 sm:w-52" />
                  </Link>
                  <div
                    className="mx-2 h-10 w-px bg-gray-200 min-[360px]:mx-4 min-[360px]:h-12 sm:mx-5 sm:h-14"
                    aria-hidden="true"
                  />
                  <div
                    className="flex flex-col items-start gap-1.5"
                    role="img"
                    aria-label="Powered by MindLabs"
                  >
                    <span className="text-[8px] font-semibold uppercase tracking-[0.12em] text-gray-500 min-[360px]:text-[9px] sm:text-[10px]">
                      Powered by
                    </span>
                    <Logo
                      className="h-5 w-auto min-[360px]:h-6 sm:h-7"
                      width={116}
                      height={28}
                      aria-hidden="true"
                    />
                  </div>
                </div>
              ) : (
                <Link href={homeHref} aria-label={`${brand.appName} home`}>
                  <BrandLogo className="h-[100px] w-[250px]" />
                </Link>
              )}
            </header>
            <main className="flex flex-1 flex-col justify-center px-4 pb-12 sm:px-6">
              <div className="mx-auto flex w-full max-w-md flex-col gap-4">
                <div className="flex rounded-full bg-gray-100 p-1">
                  <button
                    type="button"
                    onClick={() => setLoginMode('educator')}
                    className={getLoginModeButtonClass(!isStudentMode)}
                    aria-pressed={!isStudentMode}
                  >
                    Email Log-in
                  </button>
                  <button
                    type="button"
                    onClick={handleStudentMode}
                    className={getLoginModeButtonClass(isStudentMode)}
                    aria-pressed={isStudentMode}
                  >
                    Room Code Log-in
                  </button>
                </div>

                {isStudentMode ? (
                  <StudentRoomLoginForm />
                ) : (
                  <AuthForm isRegisterForm={false} providers={providers} />
                )}
              </div>
            </main>
          </div>
        </QueryClientProvider>
      </CSPostHogProvider>
    </WebViewProvider>
  );
};

export const getServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const { req } = context;
  const brand = await getServerBrandContext(req);
  const forceLogout =
    context.query.forceLogout === 'true' || context.query.forceLogout === '1';

  if (forceLogout) {
    // Proactively expire auth cookies so the user reaches a clean sign-in state.
    applyForcedLogoutHeaders(
      context.res,
      'token_present_session_hydration_failed',
      'api',
      req
    );
  } else {
    const session = await getServerSession({
      req,
      authOptions: { secret: AUTH_SECRET, providers: authOptions.providers },
    });

    if (session) {
      return {
        redirect: {
          destination: determineRedirectionPath(session),
          permanent: false,
        },
      };
    }
  }

  const providers = filterEnabledAuthProviders(await getProviders(), brand);

  return {
    props: {
      providers,
      brand,
      session: null,
    },
  };
};

export default SignIn;
